import { useAuth, authorizedFetch } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface Anatomy {
  name: string
  medical_term?: string
  parent?: string
}

export interface Symptom {
  id: string
  symptom_type: string
  anatomy: Anatomy
  severity: 'mild' | 'moderate' | 'severe' | 'very_severe'
  occurred_at: string // ISO datetime
  notes?: string
  created_at: string
}

export interface SymptomFilters {
  start_date?: string // ISO datetime
  end_date?: string // ISO datetime
  severity?: string | string[] // Single severity or array
  symptom_type?: string
  anatomy_name?: string
  limit?: number
}

export interface CreateSymptomData {
  symptom_type_name: string
  anatomy_name: string
  severity: 'mild' | 'moderate' | 'severe' | 'very_severe'
  occurred_at: string // ISO datetime
  notes?: string
  anatomy_medical_term?: string
  anatomy_parent_name?: string
}

export interface UpdateSymptomData {
  symptom_id: string
  symptom_type_name?: string
  anatomy_name?: string
  severity?: 'mild' | 'moderate' | 'severe' | 'very_severe'
  occurred_at?: string
  notes?: string
  anatomy_medical_term?: string
  anatomy_parent_name?: string
}

export function useSymptoms(filters: SymptomFilters = {}) {
  const { token } = useAuth()

  const queryKey = ['symptoms', filters]

  const symptomsQuery = useQuery({
    queryKey,
    queryFn: async (): Promise<Symptom[]> => {
      if (!token) throw new Error('Not authenticated')

      const params = new URLSearchParams()
      if (filters.start_date) params.append('start_date', filters.start_date)
      if (filters.end_date) params.append('end_date', filters.end_date)
      if (filters.severity) {
        if (Array.isArray(filters.severity)) {
          params.append('severity', filters.severity.join(','))
        } else {
          params.append('severity', filters.severity)
        }
      }
      if (filters.symptom_type) params.append('symptom_type', filters.symptom_type)
      if (filters.anatomy_name) params.append('anatomy_name', filters.anatomy_name)
      if (filters.limit) params.append('limit', filters.limit.toString())

      const url = `/api/symptoms/${params.toString() ? '?' + params.toString() : ''}`

      const response = await authorizedFetch(url)

      if (!response.ok) {
        throw new Error('Failed to fetch symptoms')
      }

      return response.json()
    },
    enabled: !!token,
    staleTime: 30 * 1000, // 30 seconds
  })

  return {
    symptoms: symptomsQuery.data || [],
    isLoading: symptomsQuery.isLoading,
    error: symptomsQuery.error,
    refetch: symptomsQuery.refetch,
  }
}

export function useSymptom(symptomId: string | null) {
  const { token } = useAuth()

  const symptomQuery = useQuery({
    queryKey: ['symptoms', symptomId],
    queryFn: async (): Promise<Symptom> => {
      if (!token || !symptomId) throw new Error('Not authenticated or no symptom ID')

      const response = await authorizedFetch(`/api/symptoms/${symptomId}`)

      if (!response.ok) {
        throw new Error('Failed to fetch symptom')
      }

      return response.json()
    },
    enabled: !!token && !!symptomId,
  })

  return {
    symptom: symptomQuery.data,
    isLoading: symptomQuery.isLoading,
    error: symptomQuery.error,
  }
}

export function useCreateSymptom() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateSymptomData): Promise<Symptom> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch('/api/symptoms/', {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to create symptom')
      }

      return response.json()
    },
    onSuccess: () => {
      // Invalidate symptoms list cache
      queryClient.invalidateQueries({ queryKey: ['symptoms'] })
    },
  })
}

export function useUpdateSymptom() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateSymptomData): Promise<Symptom> => {
      if (!token) throw new Error('Not authenticated')

      const { symptom_id, ...updateData } = data

      const response = await authorizedFetch(`/api/symptoms/${symptom_id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to update symptom')
      }

      return response.json()
    },
    onSuccess: (data) => {
      // Invalidate symptoms list and specific symptom cache
      queryClient.invalidateQueries({ queryKey: ['symptoms'] })
      queryClient.invalidateQueries({ queryKey: ['symptoms', data.id] })
    },
  })
}

export function useDeleteSymptom() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (symptomId: string): Promise<void> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/symptoms/${symptomId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to delete symptom')
      }
    },
    onSuccess: () => {
      // Invalidate symptoms list cache
      queryClient.invalidateQueries({ queryKey: ['symptoms'] })
    },
  })
}

